import React, { useEffect, useRef, useState } from "react";
import { onPolicyEvent } from "../../../../Service/realtime";
import { Dialog, Box, Typography, IconButton, Chip, CircularProgress, Button, Tooltip, Collapse } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import SouthWestRoundedIcon from "@mui/icons-material/SouthWestRounded";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import moment from "moment";
import { GetViewErrorOnlinePolicies, ResetRelianceStage } from "../../../../Service/_index";
import PartStatusList from "./PartStatusList";
import RelianceStageList from "./RelianceStageList";
import EditChanges from "./EditChanges";
import StageLogList from "./StageLogList";
import RunHistoryList from "./RunHistoryList";
import ActivityList from "./ActivityList";
import ErrorList from "./ErrorList";
import { isResubmit } from "../utils/activityText";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import { canRunNow, isRunningNow, stageParts, stageStatusMeta } from "../utils/relianceStages";
import { PartFilterChips, matchesPart } from "./PartTag";
import { hasRealParts, realParts } from "../utils/policyParts";
import { editMoneyText } from "../utils/editPayer";
import { fmtTime, statusWords, moneySentence, paymentSummary } from "../utils/historyText";

// The policy's whole story in one dialog: where it stands (status, payment, who made it, tries, edits), every rupee its
// wallets moved, a timeline of what happened, the edits and the errors. Wording: utils/historyText.js.

const FONT = "Inter, Poppins, sans-serif";
const NAVY = "#1E3A5F";
const fmt = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "—");

const TONE = {
  success: { color: "#067647", bg: "#ECFDF3", border: "#ABEFC6" },
  error: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
  warning: { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89" },
  info: { color: "#175CD3", bg: "#EFF8FF", border: "#B2DDFF" },
  navy: { color: NAVY, bg: "#EEF3F9", border: "#C9D7E8" },
  default: { color: "#344054", bg: "#F9FAFB", border: "#EAECF0" },
};

const statusTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "completed" || s === "complete") return "success";
  if (s.startsWith("failed") || s === "error") return "error";
  if (s === "processing") return "info";
  if (s === "hold" || s === "pending" || s === "retrying" || s === "completed_with_errors") return "warning";
  return "default";
};

/** A round icon badge in a tone. */
const IconBadge = ({ tone = "navy", size = 34, children }) => {
  const t = TONE[tone] || TONE.navy;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: t.color,
        backgroundColor: t.bg,
        border: `1px solid ${t.border}`,
        "& svg": { fontSize: size * 0.55 },
      }}
    >
      {children}
    </Box>
  );
};

/** A large tinted tile for the two things that matter most: where the policy stands, and whether it is paid. */
const HeroTile = ({ icon, tone = "navy", label, value, sub, testId }) => {
  const t = TONE[tone] || TONE.navy;
  return (
    <Box
      data-testid={testId}
      sx={{
        minWidth: 0,
        p: 2,
        display: "flex",
        gap: 1.75,
        alignItems: "center",
        borderRadius: "14px",
        border: `1px solid ${t.border}`,
        background: `linear-gradient(135deg, ${t.bg} 0%, #FFFFFF 100%)`,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "14px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          backgroundColor: t.color,
          boxShadow: `0 6px 14px -6px ${t.color}`,
          "& svg": { fontSize: 26 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#667085", fontFamily: FONT }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: t.color, fontFamily: FONT, lineHeight: 1.35 }}>{value}</Typography>
        {sub ? <Typography sx={{ fontSize: "12px", color: "#475467", mt: 0.25, fontFamily: FONT }}>{sub}</Typography> : null}
      </Box>
    </Box>
  );
};

/** One fact in the strip under the tiles (icon, label, value, second line). */
const Fact = ({ icon, label, value, sub }) => (
  <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", px: { xs: 0, sm: 2 }, py: { xs: 1, sm: 0 }, minWidth: 0 }}>
    <Box sx={{ color: NAVY, mt: "2px", display: "flex", "& svg": { fontSize: 20 } }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#667085", fontFamily: FONT }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: "#101828", fontFamily: FONT }}>{value}</Typography>
      {sub ? <Typography sx={{ fontSize: "11.5px", color: "#667085", fontFamily: FONT }}>{sub}</Typography> : null}
    </Box>
  </Box>
);

/** Each section's own colour, so they are told apart at a glance (2026-10-22). */
const SECTION_ACCENT = {
  runs: { color: "#3E4BB8", bg: "#EEF0FB", border: "#C7CCF2" },
  errors: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
  happened: { color: "#175CD3", bg: "#EFF8FF", border: "#B2DDFF" },
  money: { color: "#067647", bg: "#ECFDF3", border: "#ABEFC6" },
  manual: { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89" },
  edits: { color: "#6941C6", bg: "#F4F3FF", border: "#D9D6FE" },
};
const NO_ACCENT = { color: NAVY, bg: "#FCFCFD", border: "#EAECF0" };

/**
 * A section card with a header (icon, title, one line of help, count). Click the header to collapse / expand it.
 * On the page (2026-10-22): every section starts closed, each has its own colour, and its header stays at the top
 * while its content scrolls (the top of the column on a large screen, under the app bar on a phone).
 */
const Section = ({ icon, title, hint, count, children, defaultOpen = true, id, sticky = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const accent = SECTION_ACCENT[id] || NO_ACCENT;
  // a shortcut in the bar opens the section it jumps to
  useEffect(() => {
    if (!id) return undefined;
    const openMe = (e) => {
      if (e.detail === id) setOpen(true);
    };
    window.addEventListener("history:open-section", openMe);
    return () => window.removeEventListener("history:open-section", openMe);
  }, [id]);
  return (
    // no overflow clipping here: it would stop the header from sticking
    <Box data-testid={id ? `section-${id}` : undefined} sx={{ mb: 2, border: `1px solid ${accent.border}`, borderRadius: "12px", backgroundColor: "#fff" }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${title} - ${open ? "collapse" : "expand"}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          borderLeft: `4px solid ${accent.color}`,
          borderRadius: open ? "11px 11px 0 0" : "11px",
          borderBottom: open ? `1px solid ${accent.border}` : "none",
          backgroundColor: accent.bg,
          cursor: "pointer",
          userSelect: "none",
          ...(sticky ? { position: "sticky", top: { xs: 60, lg: 0 }, zIndex: 2 } : {}),
          "&:hover": { filter: "brightness(0.98)" },
        }}
      >
        <Box sx={{ color: accent.color, display: "flex", "& svg": { fontSize: 19 } }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "13.5px", color: "#101828", fontFamily: FONT, lineHeight: 1.3 }}>{title}</Typography>
          {hint && <Typography sx={{ fontSize: "11.5px", color: "#475467", fontFamily: FONT, lineHeight: 1.3 }}>{hint}</Typography>}
        </Box>
        {typeof count === "number" && (
          <Chip label={count} size="small" sx={{ height: 20, fontSize: "11px", fontWeight: 700, color: accent.color, backgroundColor: "#fff", border: `1px solid ${accent.border}` }} />
        )}
        <Typography sx={{ ml: "auto", fontSize: "11.5px", color: accent.color, fontWeight: 600, fontFamily: FONT, display: { xs: "none", sm: "block" } }}>
          {open ? "Hide" : "Show"}
        </Typography>
        <ExpandMoreRoundedIcon sx={{ ml: { xs: "auto", sm: 0 }, color: accent.color, fontSize: 20, transition: "transform .2s", transform: open ? "rotate(180deg)" : "none" }} />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 2, py: 1.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

const Empty = ({ children, tone }) => (
  <Typography sx={{ fontSize: "12.5px", color: tone === "ok" ? "#067647" : "#667085", fontFamily: FONT }}>{children}</Typography>
);


/**
 * Full history for a policy's automation job.
 *
 * The job document survives policy edits: editing a policy hard-deletes the old
 * job and creates a new one that copies the previous job's error logs, status
 * timeline and edit counters forward. So this dialog shows the complete story
 * across every edit, not just the current attempt.
 *
 * A policy has one job with a status per part (OD / TP / PA). Every entry is
 * tagged with its part; entries with none (hold, edit ...) are policy-wide.
 */
/**
 * variant "page" (2026-10-22): the same history as a full page (/onlinePolicy/view/history/:policyId - there is a lot of
 * it), not a dialog: no backdrop, no height limit, the close button is "Back to list".
 */
const JobHistoryDialog = ({ open, onClose, policyId, customerName, onChanged, variant = "dialog", actions = [] }) => {
  const asPage = variant === "page";
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [partFilter, setPartFilter] = useState("all");
  // Reliance stages: the card whose confirm buttons are showing ({ partKey, stage, checking, canRun, runWhy }), the
  // request in flight, a note when the click was refused (the automation started meanwhile ...), and a counter that
  // reloads the history after a change
  const [armed, setArmed] = useState(null);
  const [stageBusy, setStageBusy] = useState(false);
  const [stageNotice, setStageNotice] = useState(null);
  // the header's refresh: the history stays on screen while it is read again
  const [refreshing, setRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (open) setPartFilter("all");
    setArmed(null);
    setStageNotice(null);
  }, [open, policyId]);

  useEffect(() => {
    if (!open || !policyId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await GetViewErrorOnlinePolicies({ policyId });
        if (cancelled) return;
        if (res?.error) setError(res.message || "Could not load history");
        else setData(res?.data || null);
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, policyId, reloadKey]);

  // Live (2026-10-22): when the server says THIS policy changed (or the socket came back after a break), read it again
  // quietly - debounced, so a burst is one read. Never while a step change is being confirmed: that click is not reset,
  // the read waits for it. `liveAt` lights the header's "Updated" mark for a moment.
  const [liveAt, setLiveAt] = useState(0);
  const busyRef = useRef(false);
  busyRef.current = !!armed || stageBusy || refreshing;
  useEffect(() => {
    if (!open || !policyId) return undefined;
    let timer = null;
    let cancelled = false;
    const read = async () => {
      if (cancelled) return;
      if (busyRef.current) {
        timer = setTimeout(read, 2000);
        return;
      }
      const res = await GetViewErrorOnlinePolicies({ policyId }, { silent: true }).catch(() => null);
      if (cancelled || !res || res.error || !res.data) return;
      setData(res.data);
      setLiveAt(Date.now());
    };
    const off = onPolicyEvent((e) => {
      if (!(e.type === "resync" || (e.type === "changed" && String(e.policyId) === String(policyId)))) return;
      clearTimeout(timer);
      timer = setTimeout(read, 1000);
    });
    return () => {
      cancelled = true;
      off();
      clearTimeout(timer);
    };
  }, [open, policyId]);
  const [justUpdated, setJustUpdated] = useState(false);
  useEffect(() => {
    if (!liveAt) return undefined;
    setJustUpdated(true);
    const t = setTimeout(() => setJustUpdated(false), 4000);
    return () => clearTimeout(t);
  }, [liveAt]);

  const refreshHistory = async () => {
    if (refreshing || loading || !policyId) return;
    setRefreshing(true);
    setArmed(null);
    setStageNotice(null);
    try {
      const res = await GetViewErrorOnlinePolicies({ policyId });
      if (res && !res.error && res.data) {
        setData(res.data);
        setError(null);
      }
    } finally {
      setRefreshing(false);
    }
  };

  // First click on "Set back to Pending": read the policy again NOW (the dialog may have been open for a while). While the
  // automation runs it nothing is offered; "Update & run" only when it is Completed, Pending or Failed.
  const armStage = async (part, row) => {
    if (stageBusy) return;
    setStageNotice(null);
    setArmed({ partKey: part.key, stage: row.stage, checking: true });
    const res = await GetViewErrorOnlinePolicies({ policyId });
    if (!res || res.error || !res.data) {
      setArmed(null);
      return;
    }
    const fresh = res.data;
    setData(fresh);
    if (isRunningNow(fresh.status) || fresh.stageResetEnabled === false) {
      setArmed(null);
      setStageNotice("The automation is running this policy right now, so its steps cannot be changed. Try again when the run has finished.");
      return;
    }
    const freshPart = stageParts(fresh.relianceStages).find((p) => p.key === part.key);
    const freshRow = freshPart && freshPart.stages.find((r) => r.stage === row.stage);
    const now = stageStatusMeta(freshRow && freshRow.status).status;
    if (!freshRow || now === "pending") {
      setArmed(null);
      setStageNotice(`This step is ${now === "pending" ? "already Pending" : "no longer there"} — the history was refreshed.`);
      return;
    }
    const canRun = canRunNow(fresh.status);
    const held = String(fresh.status || "").toLowerCase() === "hold";
    setArmed({ partKey: part.key, stage: row.stage, checking: false, canRun, runWhy: canRun ? "" : held ? "on hold, so only the update" : "only the update now" });
  };

  // "Only update" (run = false) or "Update & run" (run = true); the service toasts the result either way
  const confirmStage = async (run) => {
    if (!armed || armed.checking || stageBusy) return;
    setStageBusy(true);
    try {
      const res = await ResetRelianceStage({ policyId, part: armed.partKey, stage: armed.stage, run: !!run && !!armed.canRun });
      if (res && !res.error) {
        if (onChanged) onChanged(res.data);
        setReloadKey((k) => k + 1);
      }
    } finally {
      setStageBusy(false);
      setArmed(null);
    }
  };

  const parts = data?.parts;
  const showParts = hasRealParts(parts);
  const hasStages = stageParts(data?.relianceStages).length > 0;
  const filterable = realParts(parts).filter((p) => p.status !== "cancelled").length > 1;
  const activeFilter = filterable ? partFilter : "all";

  const editHistory = data?.editHistory || [];
  const stageLog = data?.stageLog || [];
  // Policy-wide entries (no `part`) stay under every filter, so the counts
  // follow the filter too.
  const errorLogs = (data?.errorLogs || []).filter((e) => matchesPart(e, activeFilter));
  const statusHistory = (data?.statusHistory || []).filter((e) => matchesPart(e, activeFilter));
  const runs = (data?.runs || []).filter((r) => matchesPart(r, activeFilter));
  const walletMoves = data?.walletMoves || [];
  const pay = data ? paymentSummary(data) : null;

  // A payment-link policy can be sent through again after its job completed - nothing has been paid at that point.
  // Counted from the entries that really are such a re-submit (utils/activityText.js isResubmit: before 2026-10-22 any
  // completed -> pending counted, so a step set by hand looked like a re-submit). Several parts in one go = one.
  const resubmittedAfterCompletion = new Set(
    (data?.statusHistory || []).filter(isResubmit).map((s) => moment(s.timestamp).format("YYYY-MM-DD HH:mm"))
  ).size;

  // each section once - the dialog stacks them, the page puts them in two columns
  const render_money = () => (
    <>
            {/* Money — every wallet row of this policy (and, while it waits, what Remove hold will charge) */}
            <Section id="money" defaultOpen={!asPage} sticky={asPage} icon={<AccountBalanceWalletOutlinedIcon />} title="Money" hint="Every rupee moved for this policy" count={walletMoves.length}>
              {walletMoves.length === 0 ? (
                <Empty>{pay.tone === "error" ? `Nothing charged yet. ${pay.detail || ""}`.trim() : "No wallet money moved for this policy."}</Empty>
              ) : (
                walletMoves.map((m, i) => {
                  const line = moneySentence(m);
                  return (
                    <Box
                      key={i}
                      data-testid="money-row"
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                        py: 1,
                        borderBottom: i < walletMoves.length - 1 ? "1px dashed #EAECF0" : "none",
                      }}
                    >
                      <IconBadge tone={line.out ? "navy" : "success"} size={32}>
                        {line.out ? <NorthEastRoundedIcon /> : <SouthWestRoundedIcon />}
                      </IconBadge>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "13px", color: line.out ? NAVY : "#067647", fontWeight: 600, fontFamily: FONT }}>{line.text}</Typography>
                        <Typography sx={{ fontSize: "11.5px", color: "#667085", fontFamily: FONT }}>
                          {fmtTime(m.at)}
                          {m.byName ? ` · by ${m.byName}` : ""}
                          {m.remarks ? ` · ${m.remarks}` : ""}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: "13.5px", fontWeight: 700, fontFamily: FONT, color: line.out ? "#B42318" : "#067647", whiteSpace: "nowrap" }}>
                        {line.out ? "−" : "+"} ₹{(Math.round((Number(m.amount) || 0) * 100) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  );
                })
              )}
              {data.payment?.lastError ? (
                <Box sx={{ mt: 1.25, p: 1.25, borderRadius: "8px", backgroundColor: "#FEF3F2", border: "1px solid #FECDCA" }}>
                  <Typography sx={{ fontSize: "12px", color: "#B42318", fontFamily: FONT }}>Last Remove hold: {data.payment.lastError}</Typography>
                </Box>
              ) : null}
            </Section>

    </>
  );
  const render_happened = () => (
    <>
            {/* Activity: everything that happened, in plain words, newest first (components/ActivityList.js) */}
            <Section id="happened" defaultOpen={!asPage} sticky={asPage} icon={<TimelineRoundedIcon />} title="Activity" hint="Everything that happened to this policy, newest first" count={statusHistory.length}>
              <ActivityList entries={statusHistory} />
            </Section>
    </>
  );
  const render_runs = () => (
    <>
            {/* Every automation run: who started it, which login, how long each step took (2026-10-22) */}
            <Section id="runs" defaultOpen={!asPage} sticky={asPage} icon={<PlayCircleOutlineRoundedIcon />} title="Runs" hint="Each time the automation ran - who started it, which login, how long each step took" count={runs.length}>
              <RunHistoryList runs={runs} />
            </Section>

    </>
  );
  const render_manual = () => (
    <>
            {/* Steps changed by a person: Edit -> Status / KYC files, Set back to Pending (who, what, why) */}
            {stageLog.length || stageParts(data?.relianceStages).length ? (
              <Section id="manual" defaultOpen={!asPage} sticky={asPage} icon={<TuneRoundedIcon />} title="Manual changes" hint="Steps changed by a person, with the reason" count={stageLog.length}>
                <StageLogList entries={stageLog} />
              </Section>
            ) : null}

    </>
  );
  const render_edits = () => (
    <>
            {/* Edit history */}
            <Section id="edits" defaultOpen={!asPage} sticky={asPage} icon={<EditNoteRoundedIcon />} title="Edits" hint="Who edited the policy and what changed" count={editHistory.length}>
              {editHistory.length === 0 ? (
                <Empty>This policy has not been edited.</Empty>
              ) : (
                editHistory.map((e, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1.5, py: 1, alignItems: "flex-start", borderBottom: i < editHistory.length - 1 ? "1px dashed #EAECF0" : "none" }}>
                    <Chip label={`#${i + 1}`} size="small" sx={{ height: 22, fontSize: "11px", fontWeight: 600, color: NAVY, backgroundColor: "#EEF3F9" }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "13px", color: "#101828", fontWeight: 500, fontFamily: FONT }}>
                        Edited {e.editedByName || e.editedBy ? `by ${e.editedByName || e.editedBy}` : "(user not recorded)"}
                      </Typography>
                      <Typography sx={{ fontSize: "11.5px", color: "#667085", fontFamily: FONT }}>{fmt(e.editedAt)}</Typography>
                      {editMoneyText(e) && <Typography sx={{ fontSize: "12px", color: "#475467", fontFamily: FONT }}>{editMoneyText(e)}</Typography>}
                      {/* only the fields this edit changed, collapsed until asked for */}
                      <EditChanges changes={e.changes} more={e.changesMore} index={i} />
                    </Box>
                  </Box>
                ))
              )}
            </Section>

    </>
  );
  const render_errors = () => (
    <>
            {/* Errors: newest first, collapsible, pictures only when opened (components/ErrorList.js) */}
            <Section id="errors" defaultOpen={!asPage} sticky={asPage} icon={<ReportProblemOutlinedIcon />} title="Issues" hint="Why a run stopped, and what to do" count={errorLogs.length}>
              <ErrorList logs={errorLogs} parts={parts} showPicture={asPage} />
            </Section>
    </>
  );

  // the policy's summary (status, payment, who made it, tries, edits) and its parts / Reliance steps
  const renderSummary = () => (
    <Box id="history-summary" sx={{ scrollMarginTop: "16px" }}>
            {/* Summary — two tiles (status, payment), then a strip of facts (created, tries, edits) */}
            <Box data-testid="history-summary" sx={{ mb: 2 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mb: 1.5 }}>
                <HeroTile
                  testId="history-status"
                  icon={<SmartToyOutlinedIcon />}
                  tone={statusTone(data.status) === "default" ? "navy" : statusTone(data.status)}
                  label="Automation status"
                  value={statusWords(data.status)}
                  sub={data.statusLabel || undefined}
                />
                <HeroTile
                  testId="history-payment"
                  icon={<AccountBalanceWalletOutlinedIcon />}
                  tone={pay.tone === "default" ? "navy" : pay.tone}
                  label="Payment"
                  value={pay.label}
                  sub={pay.detail || undefined}
                />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  py: { xs: 0.5, sm: 1.5 },
                  px: { xs: 1.5, sm: 0 },
                  border: "1px solid #EAECF0",
                  borderRadius: "14px",
                  backgroundColor: "#fff",
                  "& > div + div": { borderLeft: { xs: "none", sm: "1px solid #EAECF0" }, borderTop: { xs: "1px solid #F2F4F7", sm: "none" } },
                }}
              >
                <Fact icon={<EventOutlinedIcon />} label="Created" value={fmt(data.createdAt)} sub={`by ${data.createdByName || data.createdBy || "—"}`} />
                <Fact
                  icon={<ReplayRoundedIcon />}
                  label="Automation tries"
                  value={`${data.attempts || 0} of ${data.maxAttempts || 0} used`}
                  sub={data.attempts ? undefined : "Not started yet"}
                />
                <Fact
                  icon={<EditNoteRoundedIcon />}
                  label="Edits"
                  value={data.editCount ? `${data.editCount} ${data.editCount === 1 ? "time" : "times"}` : "Never edited"}
                  sub={data.lastEditedAt ? `last ${fmt(data.lastEditedAt)} by ${data.lastEditedByName || data.lastEditedBy || "—"}` : undefined}
                />
              </Box>
              {resubmittedAfterCompletion ? (
                <Box sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: "10px", backgroundColor: "#FFFAEB", border: "1px solid #FEDF89" }}>
                  <Typography sx={{ fontSize: "12.5px", color: "#B54708", fontWeight: 600, fontFamily: FONT }}>
                    Re-submitted after completing {`${resubmittedAfterCompletion}×`}
                  </Typography>
                </Box>
              ) : null}
            </Box>

            {(showParts || hasStages) && (
              <Box sx={{ mb: 2, p: 1.5, border: "1px solid #EAECF0", borderRadius: "12px", backgroundColor: "#fff" }}>
                {/* (the status line itself is already in the status tile above) */}
                {showParts && <PartStatusList parts={parts} maxWidth={420} />}
                {stageNotice ? (
                  <Box data-testid="stage-notice" sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: "10px", backgroundColor: "#FFFAEB", border: "1px solid #FEDF89" }}>
                    <Typography sx={{ fontSize: "12.5px", color: "#B54708", fontWeight: 600, fontFamily: FONT }}>{stageNotice}</Typography>
                  </Box>
                ) : null}
                <RelianceStageList
                  relianceStages={data.relianceStages}
                  enabled={data.stageResetEnabled !== false && !isRunningNow(data.status) && !stageBusy}
                  paymentMethod={data.paymentMethod}
                  armed={armed}
                  busy={stageBusy}
                  onArm={armStage}
                  onConfirm={confirmStage}
                  onCancel={() => setArmed(null)}
                />
              </Box>
            )}

    </Box>
  );

  return (
    <Frame asPage={asPage} open={open} onClose={onClose}>
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          borderRadius: asPage ? { xs: "16px 16px 0 0", lg: 0 } : 0,
          px: { xs: 1.75, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 1.5 },
          color: "#fff",
          background: `linear-gradient(135deg, ${NAVY} 0%, #2E5A8F 100%)`,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: "12px",
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.14)",
            border: "1px solid rgba(255,255,255,0.22)",
          }}
        >
          <HistoryRoundedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: { xs: "16px", sm: "17px" }, fontFamily: FONT, lineHeight: 1.3, whiteSpace: "nowrap" }}>Policy History</Typography>
          <Typography
            sx={{ color: "rgba(255,255,255,0.78)", fontSize: "12.5px", fontFamily: FONT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: { xs: "nowrap", sm: "normal" } }}
          >
            {[customerName || "Policy", data?.policyNo].filter(Boolean).join(" · ")}
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              {" "}
              — what happened, newest first
            </Box>
          </Typography>
        </Box>
        {data?.policyNo && (
          <Chip
            label={data.policyNo}
            size="small"
            sx={{ display: { xs: "none", sm: "inline-flex" }, color: "#fff", fontWeight: 600, backgroundColor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.3)" }}
          />
        )}
        {/* updates arrive by themselves (the socket); a short "Updated" when one just did */}
        <Chip
          data-testid="history-live"
          size="small"
          label={justUpdated ? "Updated" : "Live"}
          icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: justUpdated ? "#FEC84B" : "#32D583", ml: "8px !important" }} />}
          sx={{ display: { xs: "none", sm: "inline-flex" }, color: "#fff", fontWeight: 600, fontSize: "11.5px", backgroundColor: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.28)", transition: "all .3s" }}
        />
        <Tooltip title="Refresh" arrow>
          <span>
            <IconButton
              size="small"
              onClick={refreshHistory}
              disabled={refreshing || loading}
              aria-label="Refresh"
              data-testid="history-refresh"
              sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" }, "&.Mui-disabled": { color: "rgba(255,255,255,0.6)" } }}
            >
              <RefreshRoundedIcon
                fontSize="small"
                sx={{ animation: refreshing ? "historyRefreshSpin 0.9s linear infinite" : "none", "@keyframes historyRefreshSpin": { to: { transform: "rotate(360deg)" } } }}
              />
            </IconButton>
          </span>
        </Tooltip>
        {asPage ? (
          <>
            <Button
              size="small"
              onClick={onClose}
              startIcon={<ArrowBackRoundedIcon />}
              sx={{ display: { xs: "none", sm: "inline-flex" }, color: "#fff", textTransform: "none", fontWeight: 600, border: "1px solid rgba(255,255,255,0.35)", borderRadius: "8px", whiteSpace: "nowrap", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } }}
            >
              Back to list
            </Button>
            {/* phones: the same, as an icon (the words do not fit beside the title) */}
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Back to list"
              sx={{ display: { xs: "inline-flex", sm: "none" }, color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: "8px", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } }}
            >
              <ArrowBackRoundedIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          p: { xs: 1.75, sm: 2.5 },
          backgroundColor: "#F8FAFC",
          ...(asPage
            ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: { lg: "hidden" } }
            : { maxHeight: "72vh", overflowY: "auto" }),
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 4, justifyContent: "center" }}>
            <CircularProgress size={20} sx={{ color: NAVY }} />
            <Typography sx={{ fontSize: "13px", fontFamily: FONT, color: "#475467" }}>Loading history…</Typography>
          </Box>
        )}

        {!loading && error && <Typography sx={{ color: "#B42318", fontSize: "13px", fontFamily: FONT }}>{error}</Typography>}

        {!loading && !error && !data && <Empty>No job found for this policy yet.</Empty>}

        {!loading && !error && data && (
          <>
            {asPage && (
              <HistoryShortcuts
                items={[
                  { id: "summary", label: "Summary" },
                  { id: "runs", label: "Runs", count: runs.length },
                  { id: "errors", label: "Issues", count: errorLogs.length, tone: errorLogs.length ? "error" : null },
                  { id: "happened", label: "Activity", count: statusHistory.length },
                  { id: "money", label: "Money", count: walletMoves.length },
                  { id: "manual", label: "Manual changes", count: stageLog.length },
                  { id: "edits", label: "Edits", count: editHistory.length },
                ]}
                actions={actions}
              />
            )}
            {/* the summary: on top in the dialog; the first thing in the left column on the page */}
            {!asPage && renderSummary()}
            {filterable && <PartFilterChips parts={parts} value={partFilter} onChange={setPartFilter} />}

            {asPage ? (
              // the full page: the runs and errors on the left, the story of the policy on the right; each column
              // scrolls on its own on a large screen
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1.55fr) minmax(0, 1fr)" }, gap: 2, alignItems: "start", flex: { lg: 1 }, minHeight: { lg: 0 } }}>
                <Box sx={columnSx}>
                  {renderSummary()}
                  <Anchor id="runs">{render_runs()}</Anchor>
                  <Anchor id="errors">{render_errors()}</Anchor>
                </Box>
                <Box sx={columnSx}>
                  <Anchor id="happened">{render_happened()}</Anchor>
                  <Anchor id="money">{render_money()}</Anchor>
                  <Anchor id="manual">{render_manual()}</Anchor>
                  <Anchor id="edits">{render_edits()}</Anchor>
                </Box>
              </Box>
            ) : (
              <>
                {render_money()}
                {render_happened()}
                {render_runs()}
                {render_manual()}
                {render_edits()}
                {render_errors()}
              </>
            )}
          </>
        )}
      </Box>

    </Frame>
  );
};

/**
 * The full page's shortcut bar (2026-10-22): jump to any section (with its count) and the page's own buttons (policy
 * details, error logs ...). Sticks under the app bar while the page scrolls.
 */
const HistoryShortcuts = ({ items, actions = [] }) => {
  const go = (id) => {
    // open it first if it was folded away, then jump to it
    window.dispatchEvent(new CustomEvent("history:open-section", { detail: id }));
    setTimeout(() => {
      const el = document.getElementById(`history-${id}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };
  return (
    <Box
      data-testid="history-shortcuts"
      sx={{
        flexShrink: 0,
        mb: 1.5,
        pb: 1.25,

        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        borderBottom: "1px solid #EAECF0",
      }}
    >
      <Box
        data-testid="history-shortcut-chips"
        sx={{
          display: "flex",
          gap: 0.75,
          flexWrap: { xs: "nowrap", md: "wrap" },
          flex: { xs: "1 1 100%", md: 1 },
          minWidth: 0,
          // phones: one row that scrolls sideways (wrapping made a tall column of cut-off chips)
          overflowX: { xs: "auto", md: "visible" },
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          mx: { xs: -0.25, md: 0 },
          px: { xs: 0.25, md: 0 },
        }}
      >
        {items.map((it) => (
          <Chip
            key={it.id}
            clickable
            onClick={() => go(it.id)}
            label={typeof it.count === "number" ? `${it.label} · ${it.count}` : it.label}
            size="small"
            sx={{
              flexShrink: 0,
              height: 28,
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: FONT,
              color: it.tone === "error" ? "#B42318" : NAVY,
              backgroundColor: it.tone === "error" ? "#FEF3F2" : "#fff",
              border: `1px solid ${it.tone === "error" ? "#FECDCA" : "#C9D7E8"}`,
              "&:hover": { backgroundColor: it.tone === "error" ? "#FEE4E2" : "#EEF3F9" },
            }}
          />
        ))}
      </Box>
      {actions.length > 0 && (
        <Box sx={{ display: "flex", gap: 0.75, flex: { xs: "1 1 100%", md: "0 0 auto" } }}>
          {actions.map((a) => (
            <Button
              key={a.label}
              size="small"
              variant="outlined"
              startIcon={a.icon}
              onClick={a.onClick}
              sx={{ flex: { xs: 1, md: "0 0 auto" }, textTransform: "none", fontWeight: 600, fontSize: "12px", borderRadius: "8px", color: NAVY, borderColor: "#C9D7E8", backgroundColor: "#fff", whiteSpace: "nowrap" }}
            >
              {a.label}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
};

/** A jump target of the shortcut bar. */
const Anchor = ({ id, children }) => (
  <Box id={`history-${id}`} sx={{ scrollMarginTop: { xs: "150px", lg: "8px" } }}>
    {children}
  </Box>
);

/** A column of the full page: scrolls inside itself on large screens. */
const columnSx = { minWidth: 0, height: { lg: "100%" }, overflowY: { lg: "auto" }, pr: { lg: 0.75 }, overscrollBehavior: "contain" };

/** A dialog, or (page) a plain card on the page. */
const Frame = ({ asPage, open, onClose, children }) =>
  asPage ? (
    <Box
      data-testid="history-page"
      sx={{
        borderRadius: "16px",
        // large screens: clipped to the rounded frame; smaller ones must not clip, or the shortcut bar cannot stick
        overflow: { xs: "visible", lg: "hidden" },
        border: "1px solid #EAECF0",
        backgroundColor: "#fff",
        // large screens: the page fits the window exactly (app bar 60 + page padding 80/40 + 16 below = 136) and each
        // column scrolls on its own - the window itself never scrolls; phones: one normal page
        display: "flex",
        flexDirection: "column",
        height: { lg: "calc(100vh - 136px)" },
        minHeight: { lg: 480 },
        boxSizing: "border-box",
      }}
    >
      {children}
    </Box>
  ) : (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      {children}
    </Dialog>
  );

export default JobHistoryDialog;
